import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, BellRing, AlarmClock, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CountdownUnit } from "@/pages/free-screening/components/Atoms";
import { computeSlots } from "@/pages/free-screening/lib/helpers";

/**
 * Anonymous-visitor promo that surfaces the /free-screening lead-magnet
 * on marketing pages after a short delay.
 *
 * Visual design mirrors the "DON'T WAIT / Early Support Makes a Big
 * Difference" urgency popup on /free-screening (parity requested by the
 * team so the funnel messaging stays consistent).
 *
 * Visibility rules
 * ----------------
 *   • Anonymous only — never renders for logged-in parents (guarded by
 *     useAuth + short-circuit before any timer is scheduled).
 *   • Opens 3s after the host route mounts.
 *   • Polls every 1.5s (capped at 60s) so it defers past any blocking
 *     dialog (e.g. milestones age-picker) instead of stacking on top.
 *   • Dismissal (X, CTA, backdrop) stamps `kp_fs_popup_dismissed_at` —
 *     stays quiet for 24 hours across all routes.
 */
const DISMISS_KEY = "kp_fs_popup_dismissed_at";
const COOLDOWN_MS = 24 * 60 * 60 * 1000;
const OPEN_DELAY_MS = 3000;
// 3-day fake urgency window (same seed logic as /free-screening).
const URGENCY_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < COOLDOWN_MS;
  } catch {
    return false;
  }
}

function stampDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export default function FreeScreeningPopup() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [deadline] = useState(() => Date.now() + URGENCY_WINDOW_MS);
  const [now, setNow] = useState(() => Date.now());

  // Routes where we NEVER want to interrupt the parent — the AI Video
  // Assessment page has its own funnel, and stacking the urgency popup on
  // top of the analysis overlay is jarring.
  const suppressOnPath = /^\/(ai-video-assessment|free-screening)/.test(location.pathname || "");

  // Schedule opening.
  useEffect(() => {
    if (loading) return;
    if (user) return; // signed-in parents never see the lead-magnet
    if (suppressOnPath) return; // /ai-video-assessment and /free-screening skip
    if (recentlyDismissed()) return;

    let opened = false;
    let poll = null;
    const tryOpen = () => {
      if (opened) return;
      if (typeof document !== "undefined" && document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      opened = true;
      if (poll) clearInterval(poll);
      setOpen(true);
    };
    const initial = setTimeout(tryOpen, OPEN_DELAY_MS);
    poll = setInterval(tryOpen, 1500);
    const stop = setTimeout(() => poll && clearInterval(poll), 60_000);
    return () => {
      clearTimeout(initial);
      clearTimeout(stop);
      if (poll) clearInterval(poll);
    };
  }, [user, loading, suppressOnPath]);

  // Countdown tick while popup is open.
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  if (!open) return null;

  const remaining = Math.max(0, deadline - now);
  const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remaining / (60 * 60 * 1000)) % 24);
  const mins = Math.floor((remaining / (60 * 1000)) % 60);
  const secs = Math.floor((remaining / 1000) % 60);
  const slotsLive = computeSlots();

  const close = () => {
    stampDismissed();
    setOpen(false);
  };

  const acceptAndGo = () => {
    stampDismissed();
    setOpen(false);
    navigate("/free-screening#lead-form");
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="fs-popup-title"
      data-testid="free-screening-popup"
      className="fixed inset-0 z-[85] flex items-center justify-center p-2 sm:p-6 bg-black/60 backdrop-blur-[2px] animate-[fs-fade-in_180ms_ease-out]"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <style>{`
        @keyframes fs-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes fs-pop-in  { from { opacity: 0; transform: translateY(14px) scale(0.97); }
                                to   { opacity: 1; transform: translateY(0)    scale(1);    } }
      `}</style>

      <div
        className="relative p-0 overflow-hidden border-0 rounded-3xl bg-white max-w-3xl w-[calc(100vw-1rem)] sm:w-full max-h-[95vh] flex flex-col shadow-[0_24px_60px_rgba(31,41,51,0.35)]"
        style={{ animation: "fs-pop-in 240ms cubic-bezier(0.22, 1, 0.36, 1) both" }}
      >
        {/* Close */}
        <button
          data-testid="free-screening-popup-close"
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-[#1F2933] text-white flex items-center justify-center shadow-md hover:bg-[#2F6F6A] transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-0">
            {/* Left: content */}
            <div className="sm:col-span-3 p-5 sm:p-7">
              <div className="flex items-center gap-2">
                <motion.div
                  animate={{ rotate: [0, -15, 15, -10, 10, 0] }}
                  transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 1.8 }}
                >
                  <BellRing className="h-7 w-7 sm:h-9 sm:w-9 text-rose-600" />
                </motion.div>
                <h3
                  id="fs-popup-title"
                  className="font-display text-[26px] sm:text-4xl font-extrabold text-rose-600 leading-none"
                >
                  DON&apos;T WAIT!
                </h3>
              </div>

              <h4 className="font-display text-[22px] sm:text-3xl font-extrabold text-[#2F6F6A] dark:text-teal-300 mt-3 leading-[1.1]">
                Early Support<br className="hidden sm:block" /> Makes a Big Difference
              </h4>

              <p className="mt-3 text-[13px] sm:text-[14px] text-slate-700 dark:text-slate-300 leading-relaxed">
                Book your <span className="font-extrabold text-rose-600">FREE</span> developmental screening today and take the first step towards your child&apos;s brighter future.
              </p>

              {/* Limited slots banner */}
              <div data-fs="countdown-card" className="mt-5 rounded-2xl bg-[#FFFBEB] border-2 border-[#F4C430]/60 p-3 sm:p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <motion.div
                      animate={{ rotate: [-6, 6, -6] }}
                      transition={{ duration: 0.8, repeat: Infinity }}
                      className="shrink-0"
                    >
                      <AlarmClock className="h-9 w-9 sm:h-11 sm:w-11 text-rose-600" strokeWidth={2.4} />
                    </motion.div>
                    <div className="leading-tight">
                      <p className="text-rose-600 font-display font-extrabold text-[12px] sm:text-sm tracking-wide">LIMITED SLOTS</p>
                      <p className="text-[#1F2933] dark:text-slate-100 font-display font-extrabold text-[13px] sm:text-base mt-0.5 whitespace-nowrap">BOOK NOW!</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center shrink-0">
                    <p className="text-[10px] sm:text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1">Offer Expires In:</p>
                    <div className="flex items-end gap-1 sm:gap-1.5">
                      <CountdownUnit value={days} label="DAYS" />
                      <CountdownUnit value={hours} label="HOURS" />
                      <CountdownUnit value={mins} label="MINS" />
                      <CountdownUnit value={secs} label="SECS" />
                    </div>
                  </div>
                </div>
                <div data-fs="countdown-divider" className="mt-3 pt-3 border-t border-[#F4C430]/40 flex items-center justify-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
                  <p className="text-[11px] sm:text-[12px] font-bold text-rose-600">
                    Only <span className="tabular-nums text-[13px] sm:text-base">{slotsLive}</span> slots left today
                  </p>
                </div>
              </div>

              {/* Trust strip */}
              <div data-fs="trust-strip" className="mt-4 rounded-2xl bg-[#F0FDFA] border border-[#14B8A6]/20 px-4 py-3 flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
                <ShieldCheck className="h-5 w-5 text-[#14B8A6] shrink-0" />
                <span className="text-[12px] sm:text-[13px] font-bold text-[#1F2933] dark:text-slate-200">100% Free</span>
                <span className="text-[#14B8A6]">•</span>
                <span className="text-[12px] sm:text-[13px] font-bold text-[#1F2933] dark:text-slate-200">No Obligation</span>
                <span className="text-[#14B8A6]">•</span>
                <span className="text-[12px] sm:text-[13px] font-bold text-[#1F2933] dark:text-slate-200">Expert Guidance</span>
              </div>

              {/* CTA */}
              <motion.button
                data-testid="free-screening-popup-cta"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={acceptAndGo}
                className="mt-5 w-full bg-[#0f766e] hover:bg-[#14B8A6] text-white font-display font-extrabold text-[14px] sm:text-base tracking-wide rounded-2xl py-3.5 sm:py-4 shadow-lg flex items-center justify-center gap-2 transition-colors"
              >
                YES! I WANT MY FREE SCREENING <ArrowRight className="h-5 w-5" />
              </motion.button>
              <p className="text-center text-[11px] sm:text-xs text-slate-600 dark:text-slate-400 mt-2">
                Spots are <span className="text-rose-600 font-extrabold">filling fast.</span> Secure your slot now!
              </p>
            </div>

            {/* Right: illustration */}
            <div data-fs="right-panel" className="sm:col-span-2 relative bg-gradient-to-br from-[#FFFBEB] via-[#F0FDFA] to-white min-h-[180px] sm:min-h-full overflow-hidden flex items-center justify-center">
              <div data-fs="right-panel-deco" className="absolute right-0 top-0 h-full w-[110%] bg-[#FEF3C7]/60 rounded-l-[40%]" />
              <img
                src="https://images.unsplash.com/photo-1587616211892-f743fcca64f9?w=600&q=80"
                alt="Happy children at a developmental screening"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="relative z-[1] h-[180px] sm:h-[420px] object-cover object-top"
              />
              {/* Age badge */}
              <div className="absolute top-3 right-3 sm:top-5 sm:right-5 z-[2]">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-[#F4C430] blur-[2px]" />
                  <div className="relative rounded-full bg-[#F4C430] border-2 border-dashed border-rose-500 px-3 py-2 sm:px-4 sm:py-3 text-center shadow-md">
                    <p className="text-[8px] sm:text-[10px] font-extrabold text-rose-600 leading-none tracking-tight">For Children</p>
                    <p className="font-display text-[16px] sm:text-2xl font-extrabold text-rose-600 leading-none mt-0.5">3-12</p>
                    <p className="text-[8px] sm:text-[10px] font-extrabold text-[#2F6F6A] leading-none mt-0.5">Years</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
